import styled from 'styled-components'

import { Badge } from '@/components/ui/badge'
import { ImageWithFallback } from '@/components/ui/image-with-fallback'
import { Skeleton } from '@/components/ui/skeleton'

export const FieldRow = styled.div(({ theme }) => ({
  '&:last-child': {
    borderBottom: 'none',
  },
  alignItems: 'center',
  borderBottom: `${theme.border.size['1']} solid ${theme.colors.gray400}`,
  display: 'grid',
  gap: theme.space['24'],
  gridTemplateColumns: '20rem minmax(0, 1fr)',
  minHeight: '4.8rem',
  // Note: 2px subtracted vertically to account for input border (1px each side) + row borderBottom (1px)
  padding: `calc(${theme.space['8']} - 2px) ${theme.space['16']}`,
}))

export const PhotoRow = styled(FieldRow)(() => ({
  paddingBottom: '0.8rem',
  paddingTop: '0.8rem',
}))

export const LabelWrapper = styled.div<{ $depth?: number }>(({ theme, $depth = 0 }) => ({
  alignItems: 'center',
  display: 'flex',
  gap: theme.space['6'],
  minWidth: 0,
  paddingLeft: `calc(${$depth} * ${theme.space['16']})`,
}))

export const HintIcon = styled.span(({ theme }) => ({
  alignItems: 'center',
  backgroundColor: theme.colors.blue100,
  borderRadius: theme.border.radius['circle'],
  color: theme.colors.blue500,
  cursor: 'help',
  display: 'inline-flex',
  flexShrink: 0,
  height: '2.4rem',
  justifyContent: 'center',
  width: '2.4rem',
}))

export const FieldValue = styled.div<{ $isViewOnly?: boolean }>(({ theme, $isViewOnly }) => ({
  alignItems: 'center',
  color: theme.colors.black800,
  display: 'flex',
  flexWrap: 'wrap',
  fontSize: '1.4rem',
  gap: theme.space['8'],
  minWidth: 0,
  ...($isViewOnly && { paddingLeft: theme.space['10'] }),
}))

export const ExternalLink = styled.a(({ theme }) => ({
  '&:hover': {
    textDecoration: 'underline',
  },
  color: theme.colors.blue500,
  minWidth: 0,
  overflowWrap: 'anywhere',
}))

export const PhotoImage = styled(ImageWithFallback)(({ theme }) => ({
  borderRadius: theme.border.radius['12'],
  flexShrink: 0,
  height: '10rem',
  objectFit: 'cover',
  width: '10rem',
}))

// Note: categories flow into as many columns as fit (3, 4, 5...) and never split across columns
export const CategoryGrid = styled.div(({ theme }) => ({
  '& > *': {
    breakInside: 'avoid',
    marginBottom: theme.space['16'],
  },
  columnGap: theme.space['16'],
  columnWidth: '30rem',
}))

// Note: fills row by row, so the order of the cards decides which ones share a row
export const CategoryRowsGrid = styled.div(({ theme }) => ({
  alignItems: 'start',
  display: 'grid',
  gap: theme.space['16'],
  gridTemplateColumns: 'repeat(auto-fill, minmax(30rem, 1fr))',
}))

export const CategoryRow = styled.div<{ $depth?: number }>(({ theme, $depth = 0 }) => ({
  '&:last-child': {
    borderBottom: 'none',
  },
  alignItems: 'center',
  borderBottom: `${theme.border.size['1']} solid ${theme.colors.gray400}`,
  display: 'flex',
  gap: theme.space['12'],
  justifyContent: 'space-between',
  minHeight: '4rem',
  padding: `${theme.space['4']} ${theme.space['12']} ${theme.space['4']} calc(${theme.space['12']} + ${$depth} * ${theme.space['8']})`,
}))

export const CategoryLabel = styled.div(({ theme }) => ({
  alignItems: 'center',
  color: theme.colors.primary500,
  display: 'flex',
  flex: 1,
  fontSize: '1.3rem',
  gap: theme.space['6'],
  minWidth: 0,

  '[data-component-id="Tooltip"] > button': {
    color: theme.colors.black200,

    '&:hover': {
      color: theme.colors.black500,
    },
  },
}))

export const CategoryBullet = styled.span(({ theme }) => ({
  backgroundColor: theme.colors.gray800,
  borderRadius: '50%',
  flexShrink: 0,
  height: '0.4rem',
  width: '0.4rem',
}))

export const CategoryText = styled.div(({ theme }) => ({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: theme.space['2'],
  minWidth: 0,
}))

export const CategoryMessage = styled.span<{ $error?: boolean }>(({ theme, $error }) => ({
  color: $error ? theme.colors.red500 : theme.colors.primary600,
  fontSize: '1.1rem',
}))

export const CategoryControl = styled.div(() => ({
  alignItems: 'center',
  display: 'flex',
  flexShrink: 0,
}))

// Note: an input and its action button stay on one line; the input takes the remaining width
export const FieldActions = styled.div(({ theme }) => ({
  '& > :first-child': {
    flex: 1,
    minWidth: 0,
  },
  alignItems: 'center',
  display: 'flex',
  flexBasis: '100%',
  gap: theme.space['8'],
}))

export const OffBadge = styled(Badge)(({ theme }) => ({
  backgroundColor: theme.colors.gray200,
  color: theme.colors.black400,
}))

export const PillSkeleton = styled(Skeleton)({
  borderRadius: '999px',
})
